"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import Icon from "@/components/icons";
import EventLink from "@/components/Link/EventLink/EventLink";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import "./styles.css";
import testimonialIcon from "@/public/icons/testimonial-icon.svg";

// Color mappings for different background themes
const backgroundColors = {
  elm: "bg-primary-500",
  putty: "bg-putty-100",
  "putty-300": "bg-putty-300",
  fern: "bg-secondary-500",
  white: "bg-white",
};

const textColors = {
  elm: "text-white",
  fern: "text-white",
  putty: "",
  "putty-300": "",
  white: "",
};

const cardBackgroundColors = {
  elm: "bg-white",
  putty: "bg-white",
  "putty-300": "bg-white",
  fern: "bg-white",
  white: "bg-putty-100",
};

const controlBackgroundColors = {
  elm: "bg-white",
  putty: "bg-white",
  "putty-300": "bg-white",
  fern: "bg-white",
  white: "bg-putty-100",
};

const defaultEventConfig = {
  name: "social-proof-link",
  htmlId: "social-proof-link",
  location: "homepage",
  elementType: "social-proof-link",
};

export type SocialProofBackground =
  | "elm"
  | "putty"
  | "putty-300"
  | "fern"
  | "white";

interface EventConfig {
  name: string;
  htmlId: string;
  location: string;
  elementType: string;
}

interface SocialProofProps {
  useAutoplay?: boolean;
  autoplayDelay?: number;
  header: string;
  headerTextClassname?: string;
  items: SlideContent[];
  linkText: string;
  linkUrl: string;
  background?: SocialProofBackground;
  location?: string;
  containerClassName?: string;
  isSideRailShown?: boolean;
  eventConfig?: EventConfig;
}

interface SlideContent {
  content: string;
  image: string;
  name: string;
  productName: string;
}

const getEmblaOptions = (isSideRailShown?: boolean): EmblaOptionsType => {
  return {
    loop: false,
    align: "start",
    containScroll: false,
    slidesToScroll: 1, // Mobile: scroll 1 slide
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 2 }, // Medium: scroll 2 slides
      "(min-width: 1024px)": { slidesToScroll: isSideRailShown ? 2 : 3 }, // Large: scroll 2 or 3 slides based on side rail
    },
  };
};

// Carousel hooks (copied from homepage hero implementation)
type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (
    emblaApi: EmblaCarouselType,
    direction: "prev" | "next" | "dot",
  ) => void,
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      if (onButtonClick) onButtonClick(emblaApi, "dot");
    },
    [emblaApi, onButtonClick],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (
    emblaApi: EmblaCarouselType,
    direction: "prev" | "next",
  ) => void,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (onButtonClick) onButtonClick(emblaApi, "prev");
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (onButtonClick) onButtonClick(emblaApi, "next");
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

export default function SocialProof({
  useAutoplay = true,
  autoplayDelay = 5000,
  header,
  headerTextClassname = "elm-h2 text-center mb-10",
  items,
  linkText,
  linkUrl,
  background = "putty",
  location = "homepage",
  containerClassName = "py-20 px-4 md:px-10 mx-break-out lg:px-[120px]",
  isSideRailShown = false,
  eventConfig = defaultEventConfig,
}: SocialProofProps) {
  const plugins = useAutoplay ? [Autoplay({ delay: autoplayDelay })] : [];
  const options = getEmblaOptions(isSideRailShown);
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  // Track expanded state for each card
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const handleExpandChange = useCallback(
    (index: number, isExpanded: boolean) => {
      setExpandedCards((prev) => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.add(index);
        } else {
          newSet.delete(index);
        }
        return newSet;
      });
    },
    [],
  );

  const { elementViewed } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Testimonials Module",
    },
  });

  const { elementClicked: prevNavButtonClicked } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Previous",
    },
  });

  const { elementClicked: nextNavButtonClicked } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Next",
    },
  });

  const { elementClicked: dotNavButtonClicked } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Dot",
    },
  });

  const onNavButtonClick = useCallback(
    (emblaApi: EmblaCarouselType, direction: "prev" | "next" | "dot") => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;
      autoplay.reset();
      switch (direction) {
        case "prev":
          prevNavButtonClicked();
          break;
        case "next":
          nextNavButtonClicked();
          break;
        case "dot":
          dotNavButtonClicked();
      }
    },
    [prevNavButtonClicked, nextNavButtonClicked, dotNavButtonClicked],
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  const { ref: socialProofRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const hasTriggeredViewEvent = useRef(false);

  useEffect(() => {
    if (inView && !hasTriggeredViewEvent.current) {
      elementViewed();
      hasTriggeredViewEvent.current = true;
    }
  }, [inView, elementViewed]);

  // Dynamic color classes based on background prop
  const backgroundColorClass =
    backgroundColors[background] || backgroundColors.putty;
  const textColor = textColors[background] || textColors.putty;
  const cardBackgroundColor =
    cardBackgroundColors[background] || cardBackgroundColors.putty;
  const controlBackgroundColor =
    controlBackgroundColors[background] || controlBackgroundColors.putty;

  const [currentSlidesToScroll, setCurrentSlidesToScroll] = useState(1);

  useEffect(() => {
    const updateSlidesToScroll = () => {
      if (window.innerWidth >= 1024) {
        setCurrentSlidesToScroll(isSideRailShown ? 2 : 3);
      } else if (window.innerWidth >= 768) {
        setCurrentSlidesToScroll(2);
      } else {
        setCurrentSlidesToScroll(1);
      }
    };

    updateSlidesToScroll();
    window.addEventListener("resize", updateSlidesToScroll);
    return () => window.removeEventListener("resize", updateSlidesToScroll);
  }, [isSideRailShown]);

  const shouldShowControls = items.length > currentSlidesToScroll;

  return (
    <div
      ref={socialProofRef}
      className={`embla social-proof ${backgroundColorClass} flex flex-col items-center ${containerClassName} ${isSideRailShown ? "side-rail-shown" : ""}`}
    >
      {header && (
        <h2 className={`${headerTextClassname} ${textColor}`}>{header}</h2>
      )}

      {/* Slides */}
      <div
        id="carousel-container"
        className="embla__viewport w-full md:w-screen lg:w-full lg:max-w-[1200px] mx-auto px-2"
        ref={emblaRef}
        role="region"
        aria-label="Social proof carousel"
        aria-roledescription="carousel"
        style={{
          overflow: "hidden",
        }}
      >
        <div className="embla__container flex">
          {items.map((slide, index) => {
            const isExpanded = expandedCards.has(index);

            return (
              <div
                key={index}
                aria-label={`Slide ${index + 1}`}
                aria-roledescription="slide"
                aria-hidden={index === selectedIndex ? false : true}
                className="embla__slide md:w-[100px] lg:w-[160px]"
              >
                <div
                  className={`p-5 ${cardBackgroundColor} rounded-xl flex flex-col border border-lightgray-200 h-[300px] transition-all duration-300`}
                >
                  <div className="flex flex-row gap-4">
                    <Icon
                      type="format-quote"
                      svgProps={{
                        width: 32,
                        height: 32,
                        className: "text-brand-500 scale-x-[-1] flex-shrink-0",
                      }}
                    />
                    <ExpandableBlock
                      maxHeight={126} // Roughly 5 lines at text-base size
                      blur={true}
                      centered={false}
                      contentClassName="text-base text-gray-600"
                      btnClassName="text-primary-500 hover:text-primary-700 text-sm"
                      className="flex-1"
                      onExpandChange={(expanded) =>
                        handleExpandChange(index, expanded)
                      }
                    >
                      {slide.content}
                    </ExpandableBlock>
                  </div>

                  {/* User info section - hidden when expanded */}
                  <div
                    className={`flex flex-col gap-4 mt-auto pt-4 transition-all duration-300 ${
                      isExpanded
                        ? "opacity-0 max-h-0 overflow-hidden pt-0"
                        : "opacity-100 max-h-none"
                    }`}
                  >
                    <div className="h-px w-full bg-putty-400" />
                    <div className="flex flex-row items-center gap-4">
                      <div className="h-[68px] w-[68px] rounded-full bg-primary-500 flex items-center justify-center overflow-hidden">
                        {slide.image ? (
                          <Image
                            src={slide.image}
                            alt={slide.name}
                            width={68}
                            height={68}
                            className="rounded-full object-cover w-full h-full"
                          />
                        ) : (
                          <Image
                            src={testimonialIcon}
                            height={67}
                            width={67}
                            alt=""
                            className="m-0"
                          />
                        )}
                      </div>
                      <div className="flex flex-col text-sm font-bold">
                        <div>{slide.name}</div>
                        <div>{slide.productName}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Controls - Only show if there are enough slides */}
      {shouldShowControls && (
        <div className="w-full px-8 mt-10 flex flex-row justify-center">
          <div
            className={`flex flex-row justify-center w-fit items-center gap-2 ${controlBackgroundColor} rounded-full`}
          >
            {/* Previous Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              aria-label="Previous slide"
              className="hover:bg-transparent"
            >
              <ChevronLeft />
            </Button>

            {/* Dots */}
            <div className="flex gap-6">
              {scrollSnaps.map((_, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="icon"
                  onClick={() => onDotButtonClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`h-3 w-3 rounded-full p-0 ${
                    index === selectedIndex ? "bg-brand-500" : "bg-primary-500"
                  }`}
                >
                  <Circle
                    svgProps={{
                      width: "8",
                      height: "8",
                      className: `${index === selectedIndex ? "text-brand-500" : "text-primary-500"}`,
                    }}
                  />
                </Button>
              ))}
            </div>

            {/* Next Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              aria-label="Next slide"
              className="hover:bg-transparent"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}

      {linkText && linkUrl && (
        <EventLink
          href={linkUrl}
          text={linkText}
          html_id={eventConfig.htmlId}
          element_type={eventConfig.elementType}
          location={eventConfig.location}
          name={eventConfig.name}
          class_name={`text-center mt-10 underline hover:text-primary-500 hover:no-underline hover:font-bold underline-offset-4 text-base ${textColor === "text-white" ? "text-white" : "text-primary-500"}`}
        />
      )}
    </div>
  );
}
